import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ExportButton from '../feedback/ExportButton';
import './timeseries.css';

export interface TimeSeriesPoint {
  date: string | number | Date;
  value: number | null;
}

export interface TimeSeriesProps {
  title?: string;
  data: TimeSeriesPoint[];
  fileName?: string;
  heightClassName?: string;
  nullLine?: boolean;
}

function computeAxisWithBuffer(values: number[]) {
  if (!values.length) return { min: 0, max: 0 };
  const minRaw = Math.min(...values);
  const maxRaw = Math.max(...values);
  const range = maxRaw - minRaw;
  const buffer = range * 0.1;
  return {
    min: minRaw === 0 ? 0 : Math.floor(minRaw - buffer),
    max: maxRaw === 0 ? 0 : Math.ceil(maxRaw + buffer),
  };
}

const TimeSeries: React.FC<TimeSeriesProps> = ({
  title,
  data,
  fileName,
  heightClassName = 'h-64',
  nullLine,
}) => {
  if (!data || data.length === 0) return null;

  const values = data
    .map((p) => p.value)
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
  const { min, max } = computeAxisWithBuffer(values);

  // build export fields
  const exportFields = Object.keys(data[0]);

  return (
    <div
      className={`${heightClassName} w-full rounded border border-gray-200 bg-white p-2 px-4 shadow-sm`}
    >
      <div className="mb-3 flex w-full items-center justify-between">
        <h3 className="truncate font-semibold text-gray-800">{title}</h3>
        {data?.length > 0 && (
          <div className="ml-4 shrink-0">
            <ExportButton
              fileName={
                fileName ||
                `${title || 'time-series'}-${data[0].date}-${data[data.length - 1].date}`
              }
              exportData={data as unknown as Record<string, unknown>[]}
              fields={exportFields}
              csvTitle={title}
            />
          </div>
        )}
      </div>

      <div className="h-[calc(100%-2rem)]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 0, right: 30, left: 20, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
            <XAxis
              dataKey="date"
              tick={{
                fontSize: 10,
                fill: '#4B5563',
                fontWeight: 500,
                dy: 6,
                textAnchor: 'end',
              }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => {
                const date = new Date(value);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              }}
              angle={-45}
            />
            <YAxis
              dataKey="value"
              domain={[min, max]}
              tick={{ fontSize: 10, fill: '#4B5563', fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => v.toLocaleString()}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                borderRadius: '0.25rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #E5E7EB',
                padding: '12px',
              }}
              labelStyle={{
                color: '#111827',
                fontWeight: 600,
                fontSize: '14px',
                marginBottom: '4px',
                textTransform: 'capitalize',
              }}
              itemStyle={{
                color: '#374151',
                fontSize: '13px',
                marginTop: '4px',
                textTransform: 'capitalize',
              }}
              formatter={(value: number) => value.toLocaleString()}
              labelFormatter={(label) => `Date: ${label}`}
            />

            <Line
              type="step"
              dataKey="value"
              stroke="#6366F1"
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 1 }}
              activeDot={{
                r: 5,
                stroke: '#6366F1',
                strokeWidth: 2,
                fill: '#6366F1',
              }}
              connectNulls={false}
            />
            {nullLine && (
              <Line
                type="step"
                dataKey="value"
                stroke="#6366F1"
                strokeWidth={1}
                strokeOpacity={0.5}
                strokeDasharray="5 5"
                dot={false}
                connectNulls={true}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TimeSeries;
